/**
 * @fileOverview The ways to the app's views: the clicks a person makes to get to one from anywhere in
 * the app — the drawer entry, a row in its list, a button, a tab. One per route, shared by every tour.
 * Pure data. See docs/view-help.md, "The ways".
 *
 * Written per tour, the same two clicks to the sample project stood in ten files, and the copies
 * drifted: the pause the Aktionen menu needs before its item can be pressed was added to two of the
 * five copies of the way to a project's cost page. And a tour only wrote the ways it walked forwards,
 * so going back to a view with none — the list the walkthrough opened on — reloaded the frame.
 */

import { CATALOG_PACKETS, PACKET_TYPES } from "@/services/catalogPackets"
import { timeTrackingEntryRoute } from "@/services/navigation"
// Not `samples/world`: that module is loaded only inside the frame, and the ways ship to every reader.
import { WORLD_PACKETS } from "@/services/viewHelp/samples/worldPackets"
import type { ViewHelpAction, ViewHelpLocation, ViewHelpTour } from "./types"

/** A way, from where the step is read: the record whose row is clicked comes from its `params`. */
export type ViewHelpWay = (_at: ViewHelpLocation) => ViewHelpAction[]

const click = (target: string): ViewHelpAction => ({ do: "click", target })

/** The drawer entry of a route — `LewNavigationBar` marks every one `nav-<route>`. */
const drawer = (route: string) => click(`nav-${route}`)

/** A view that has its own drawer entry. Clicking the entry of the page the frame is on is harmless. */
const entry =
  (route: string): ViewHelpWay =>
  () => [drawer(route)]

/**
 * A record's page: its list's drawer entry, then the record's row. The id is the step's own `param` —
 * so a tour that reads a different sample customer clicks that customer's row, with no way of its own.
 * No id, no way: the step is then loaded instead (and names no params, which development reports).
 */
const record =
  (list: string, param: string, row: (_id: string) => string = id => `row-${id}`): ViewHelpWay =>
  at => {
    const id = at.params?.[param]
    return id ? [drawer(list), click(row(id))] : []
  }

/** A create view reached through its list's button. */
const create =
  (list: string, ...steps: (string | ViewHelpAction)[]): ViewHelpWay =>
  () => [drawer(list), ...steps.map(step => (typeof step === "string" ? click(step) : step))]

/**
 * A catalogue's create form (`LewCatalogCreateButton`): the one create button over its table, then the
 * entry that opens the form — a record of the catalogue, or a packet of it. The pause is for the menu,
 * as on a detail page's Aktionen: its entries are in the document before the overlay has faded in, and
 * an anchor at opacity 0 is refused as hidden.
 */
const catalogCreate = (list: string, item: string): ViewHelpWay =>
  create(list, "catalog-create", { do: "wait", ms: 400 }, item)

/**
 * The Zeiterfassung has one drawer entry; its other screens hang off the feature's own tab strip.
 * Inside the time-tracking tour the strip is walked directly (`clickThroughToRoute`); this is the way
 * there from anywhere else. The entry is Meine Zeiten for somebody who records their own day and the
 * review queue for a reviewer kept out of recording (`timeTrackingEntryRoute`), and it is marked by the
 * route it leads to — so the way asks the same question, rather than looking for a "Meine Zeiten" the
 * reviewer's drawer does not have.
 */
const timeTrackingTab =
  (route: string): ViewHelpWay =>
  () => {
    const entryRoute = timeTrackingEntryRoute().name
    return entryRoute === route ? [drawer(route)] : [drawer(entryRoute), click(`tt-tab-${route}`)]
  }

const toProject = record("ProjectOverview", "projectId")

/**
 * The supplier overview's other two views: no drawer entry of their own, but a button each in the switch
 * on the overview's heading line (`LewSupplierViewSwitch`, `supplier-view-<route>`).
 */
const supplierView = (route: string): ViewHelpWay => create("SupplierOverview", `supplier-view-${route}`)

export const WAYS: Record<string, ViewHelpWay> = {
  // ── the drawer's own entries ──────────────────────────────────────────────
  EmployeeOverview: entry("EmployeeOverview"),
  CustomerOverview: entry("CustomerOverview"),
  ProjectOverview: entry("ProjectOverview"),
  HourlywageinvoiceWizard: entry("HourlywageinvoiceWizard"),
  FileBrowser: entry("FileBrowser"),
  ProjectCosts: entry("ProjectCosts"),
  PersonnelPlanning: entry("PersonnelPlanning"),
  TimeTrackingMy: entry("TimeTrackingMy"),
  DaySubmissionOverview: entry("DaySubmissionOverview"),
  // CategoryOverview has none: it left the drawer for the command palette, which a pointer cannot walk,
  // so a step there loads the frame onto it.
  MaterialOverview: entry("MaterialOverview"),
  MachineOverview: entry("MachineOverview"),
  SupplierOverview: entry("SupplierOverview"),
  SupplierComparison: supplierView("SupplierComparison"),
  AdditionalEffortOverview: entry("AdditionalEffortOverview"),
  UserPositionOverview: entry("UserPositionOverview"),
  // Settings keeps its tab in the hash, and each tab marks itself `tab-<hash>` (`LewTabs`): the step's
  // own hash says which one to press, so every tour lands on the tab it is about.
  SettingsOverview: at => [
    drawer("SettingsOverview"),
    ...(at.hash ? [{ do: "openTab", target: `tab-${at.hash}` } as ViewHelpAction] : []),
  ],

  // ── a record's page ───────────────────────────────────────────────────────
  // To the page and no further: a register on it is pressed by the step itself, where the reader
  // can see which tab it is.
  ProjectDetail: toProject,
  CustomerDetail: record("CustomerOverview", "customerId"),
  EmployeeDetail: record("EmployeeOverview", "employeeId", id => `employee-row-${id}`),
  MaterialDetail: record("MaterialOverview", "materialId"),
  MachineDetail: record("MachineOverview", "machineId"),
  SupplierDetail: record("SupplierOverview", "supplierId"),
  // A packet's row is in its catalogue's table, so the way asks the sample world which catalogue.
  PacketDetail: at => {
    const id = at.params?.packetId
    const packet = WORLD_PACKETS.find(it => it.id === id)
    return packet ? [drawer(CATALOG_PACKETS[packet.type].catalog), click(`row-${packet.id}`)] : []
  },
  UserPositionDetail: record("UserPositionOverview", "userPositionId"),
  DaySubmissionDetail: record("DaySubmissionOverview", "daySubmissionId"),
  // The board is opened through the calendar card's own "open" link, not through the card, whose first
  // control is the pin — and pinning is a write.
  PersonnelCalendar: record("PersonnelPlanning", "calendarId", id => `planning-open-${id}`),
  // A project's own cost page has one entry point in the app: the Aktionen menu of the project's page
  // (the drawer's Projektkosten opens the portfolio over every project). The scroll waits for the
  // register bar and with it for the record — "Aktionen" is disabled while the project still loads,
  // and a press that lands then opens no menu. The pause is for the menu: its items are in the
  // document before the overlay has faded in, and an anchor at opacity 0 is refused as hidden.
  ProjectCostsForOne: at => {
    const way = toProject(at)
    if (!way.length) return []
    return [
      ...way,
      { do: "scroll", target: "project-tabs", to: "reveal" },
      click("project-actions"),
      { do: "wait", ms: 400 },
      click("project-action-costs"),
    ]
  },

  // ── creating ──────────────────────────────────────────────────────────────
  CustomerCreate: create("CustomerOverview", "customer-create"),
  ProjectCreate: create("ProjectOverview", "project-create"),
  MaterialCreate: catalogCreate("MaterialOverview", "material-create"),
  MachineCreate: catalogCreate("MachineOverview", "machine-create"),
  SupplierCreate: create("SupplierOverview", "supplier-create"),
  // The setup has no entry but the overview's invitation, shown while it is not done.
  SupplierSetup: create("SupplierOverview", "supplier-setup-start"),
  AdditionalEffortCreate: catalogCreate("AdditionalEffortOverview", "additionaleffort-create"),
  PersonnelPlanningCreate: create("PersonnelPlanning", "planning-create"),
  // Packets are listed in their catalogue's table, and created from the same button as its records:
  // the menu's "Neues Paket erstellen" opens that kind's form.
  ...Object.fromEntries(
    PACKET_TYPES.map(type => [
      CATALOG_PACKETS[type].create,
      catalogCreate(CATALOG_PACKETS[type].catalog, "packet-create"),
    ])
  ),

  // ── the Zeiterfassung's other screens ─────────────────────────────────────
  TimeTrackingReview: timeTrackingTab("TimeTrackingReview"),
}

/**
 * The clicks to `route`, read at `at`: the tour's own way if it has one — for a way that only makes
 * sense inside that tour — or else the shared one. An empty list when there is neither, and the frame
 * is loaded onto the view instead; a tour's own empty list asks for exactly that.
 *
 * `from` is the route the frame is on. A way that opens with the way to that route starts after it:
 * somebody already on the employee list clicks the row, not "Mitarbeiter" in the drawer first. The
 * click was harmless, but the pointer travelling out to the drawer and back to the very list it left
 * read as a detour. The way to a route is usually its drawer entry alone; for a catalogue's packet
 * list it is the catalogue's entry and the switch, so on that list a way starting with both starts
 * after them. A route whose way needs a record (`at`) has none to compare, and nothing is skipped.
 *
 * A tour's way keyed by `from` applies only on that route; anywhere else the shared way is used, and
 * with none the frame is loaded onto the view — quietly, as for any view without a way.
 */
export function wayTo(
  tour: ViewHelpTour | null | undefined,
  route: string,
  at: ViewHelpLocation,
  from?: string | null
): ViewHelpAction[] {
  const own = tour?.ways?.[route]
  const ownWay = Array.isArray(own) ? own : from ? own?.from[from] : undefined
  const way = ownWay ?? WAYS[route]?.(at) ?? []
  if (!from) return way
  const here = WAYS[from]?.({}) ?? [drawer(from)]
  const startsHere = here.every((action, i) => {
    const step = way[i]
    return step?.do === "click" && action.do === "click" && step.target === action.target
  })
  return startsHere ? way.slice(here.length) : way
}
